import { AllNullable, PagedApiResponse } from "types";
import { QueryKey, UseQueryOptions, useQuery } from "@tanstack/react-query";
import { QueryParser, ValueParser } from "utils/url/querystring";
import { usePager, useQueryStringNav } from "utils";

import { CSPaginationResult } from "components/shared/pagination/CSPagination";
import { useEffect } from "react";

type QueryParserWithPager = QueryParser & {
  page: ValueParser<number, number | null>;
  size: ValueParser<number, number | null>;
};

type UseSearchPageArgs<TParser extends QueryParserWithPager, TData> = {
  baseRoutePath: string;
  queryParser: TParser;
  getQueryKey: (x: { [key in keyof TParser]: TParser[key]["defaultValue"] }) => QueryKey;
  getData: (x: { [key in keyof TParser]: TParser[key]["defaultValue"] }) => Promise<PagedApiResponse<TData>>;
  onError: UseQueryOptions["onError"];
  sortOptions: { text: string; value: string }[];
  sortTerm?: string;
};

/**
 * High level hook, specific to search pages, which combines a few elements:
 *
 * - Managing state in the query string
 * - Managing sorting and paging data
 * - Fetching data using react-query
 *
 * The return of this hook bundles a lot of data into a `searchResultsProps` property.
 * This is designed to be spread onto the <SearchResults> component,
 * but can obviously be used however it is needed.
 *
 * This hook is itself composed of other hooks so if this specific set up does not work well
 * for your page that is OK. You may just need to assemble something more custom
 * from the building blocks of this hook.
 *
 * @see `useQueryStringNav`
 * @see `usePager`
 *
 * @example
 *
 * const { queryParams, handleSearch, searchResultsProps } = useSearchPage({
 *   getQueryKey: (params) => ['my-search-page', params],
 *   getData: (params) => API.search(params),
 *   baseRoutePath: routePaths.MY_SEARCH_PAGE,
 *   queryParser: {
 *     query: qs.stringParserWithDefault(''),
 *     page: qs.numberParserWithDefault(1),
 *     size: qs.numberParserWithDefault(10),
 *   },
 *   sortOptions,
 *   onError: err => {},
 * });
 *
 * @returns Record containing data and handlers needed for easily wiring a search page.
 */
const useSearchPage = <TParser extends QueryParserWithPager, TData>({
  baseRoutePath,
  queryParser,
  getQueryKey,
  getData,
  onError,
  sortOptions = [],
  sortTerm = "sortTerm",
}: UseSearchPageArgs<TParser, TData>) => {
  const { queryParams, navigate } = useQueryStringNav(baseRoutePath, queryParser);

  const pager = usePager({ page: queryParams.page, size: queryParams.size });

  const queryResult = useQuery(getQueryKey(queryParams), () => getData(queryParams), {
    // TODO: React Query has deprecated `onSuccess` and `onError`
    // instead, we should likely use `useEffect` to watch for changes in `data` and `error`.
    // I am pointing this out while updating to v4, but not making the change now to avoid risk.
    // We can circle back to this after the upgrade and decide how to address.
    // NOTE: query-level `onSuccess` still fires after unmount, so `pager.set` causes "state update
    // on an unmounted component" warnings in tests that end while a search is in flight.
    onSuccess: (data) => pager.set(data),
    onError,
  });

  // covers initial load when search data is cached.
  // that situation does NOT trigger `onSuccess` from react-query,
  // but we can use the cached data to immediately hydrate the pager.
  useEffect(() => {
    if (queryResult.data && !pager.data) {
      pager.set(queryResult.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (params: AllNullable<Partial<{ [key in keyof TParser]: TParser[key]["defaultValue"] }>>) => {
    pager.update({ page: 1, size: params.size ?? 10 });
    navigate({ ...params, page: 1 });
  };

  return {
    ...queryResult,
    handleSearch,
    queryParams,
    searchResultsProps: {
      data: queryResult.data?.results,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
      // need to cast here as the variable `sortTerm` is not strongly typed to a key in the queryParams.
      // there is probably a way to make that work - but casting is a good compromise for now.
      sortTerm: queryParams[sortTerm] as TParser["sortTerm"]["defaultValue"],
      sortOptions,
      onSortChange: (sortTerm: string) => handleSearch({ ...queryParams, sortTerm }),
      pagerData: pager.data,
      onPageChange: ({ to }: CSPaginationResult) => {
        pager.update({ page: to });
        navigate({ ...queryParams, page: to });
      },
      onPageSizeChange: (size: number) => handleSearch({ ...queryParams, size }),
      ...pager.paginationTotals,
    },
  };
};

export default useSearchPage;
